/**
 * 智能工具「文件合规审查系统」/「鉴定评审智能辅助系统」准入判定与离站出口统一收口
 *
 * 所有离站跳转（登录/注册成功、企业选择完成、已登录点击智能工具、守卫自动弹到子站）一律调用 goLoginRedirect：
 * 由总入口本地判定账户等级，有权限就在**新标签**打开工具系统——新标签只有这一条历史记录，
 * 地址栏后退按钮天然灰掉，用户不可能退回总入口再被守卫弹一次；无权限或判定失败则保留待办标记，
 * 按原流程在当前标签落到个人版/企业版子站，由子站守卫提示或用最新信息重判。
 *
 * 密码/验证码登录从点击到返回仅几百毫秒，window.open 仍在手势激活窗口内，标签可自开；
 * 微信扫码登录几十秒 + iframe 导航后回调已无手势，openToolTab 返回 null，
 * 退化为当前标签整页直跳合规/评审，或保留 pc/pr 凭证交子站守卫/组件消费（confirm 弹窗「立即打开」供用户手势触发）。
 *
 * 当前标签的跳转一律 location.replace 而非 window.location.href 赋值：
 * href 会把总入口这一跳留在浏览器历史栈里，后退会退回 tgt 再被路由守卫弹到子站，形成一次莫名的中转；
 * replace 是整页替换、不入栈。
 */
import store from '@/store/index';
import CUser from '@/utils/User';
import { getComplianceUrl, getRedirectUrl, hasPendingCompliance, clearPendingCompliance, hasPendingReview, clearPendingReview, REVIEW_URL, LOGIN_TYPE_PERSONAL } from '@/data/siteConfig';

// 个人版：VIP 且未过期（levelId 非 0 且 levelEnd 晚于当前时间）
// 总入口路由守卫不加载用户信息（state.isVIP 恒为 false），故此处主动拉取一次
async function isPersonalVipActive() {
	try {
		const res = await store.dispatch('User/getUserInfo');
		const data = (res && res.data) || {};
		return !!(data.levelId && data.levelId !== 0 && data.levelEnd && new Date(data.levelEnd).getTime() > Date.now());
	} catch (e) {
		// 接口异常时不武断判成无权限，交给子站守卫用最新信息重判，避免误挡有权限用户
		return false;
	}
}

// 企业版：旗舰版（corporateLevel==2）且未过期；文件合规审查系统主/子账号均可单独使用，故不看 isMain
function isFlagshipActive(cinfo) {
	const info = cinfo || CUser.getCorporate();
	return !!(info && info.corporateLevel == 2 && info.endTime && new Date(info.endTime).getTime() > Date.now());
}

// 鉴定评审智能辅助系统：企业账号 + 旗舰版（corporateLevel==2）且未过期（endTime）且主账号/购买账号（isBuy/isMain）；个人账号无权限（企业版专享）
function isReviewAllowed(cinfo) {
	const info = cinfo || CUser.getCorporate();
	return !!(info && info.corporateLevel == 2 && info.endTime && new Date(info.endTime).getTime() > Date.now() && (info.isBuy == 1 || info.isMain == 1));
}

/**
 * 计算离站目标地址：有权限返回合规系统地址，否则返回原定的个人版/企业版地址；
 * 鉴定评审（review）有权限且占位标签预开成功时一并消费其标记，由 goLoginRedirect 在新标签打开。
 * @param {string} targetUrl - 目标子站地址（SITE_URL.PERSON / SITE_URL.COMPANY）
 * @param {string} token - 登录 token
 * @param {object} [cinfo] - 企业信息（企业版跳转携带，同步可用于等级判定）
 * @param {boolean} reviewPreopened - 鉴定评审占位标签是否预开成功（失败时标记保留，随 getRedirectUrl 转 pr 凭证交子站）
 * @returns {Promise<{url: string, toCompliance: boolean, reviewAllowed: boolean}>} 最终地址 + 是否直跳合规 + 鉴定评审是否可填标签
 */
async function resolveLoginRedirect(targetUrl, token, cinfo, reviewPreopened) {
	// 绝大多数离站没有打标，直接走原逻辑（同步返回，不引入额外等待）
	if (!hasPendingCompliance() && !hasPendingReview()) {
		return { url: getRedirectUrl(targetUrl, token, cinfo), toCompliance: false, reviewAllowed: false };
	}
	const tk = token || CUser.getToken();
	const isPersonal = CUser.getLoginType() === LOGIN_TYPE_PERSONAL;
	// 合规判定：个人版 VIP 未过期 / 企业版旗舰版未过期
	const compAllowed = hasPendingCompliance() ? (isPersonal ? await isPersonalVipActive() : isFlagshipActive(cinfo)) : false;
	// 鉴定评审判定：仅企业账号（旗舰版 + 主账号/购买账号）；个人账号一律无权限
	const reviewAllowed = hasPendingReview() ? (!isPersonal && isReviewAllowed(cinfo)) : false;
	// 鉴定评审判定通过且占位标签预开成功：立即消费标记，getRedirectUrl 不再转 pr（由 goLoginRedirect 直接填标签）；
	// 预开被拦时保留标记，随 getRedirectUrl 转 pr 凭证交给子站重判/打开
	if (reviewAllowed && reviewPreopened) clearPendingReview();
	if (compAllowed) {
		// 判定通过才消费合规标记，未通过时标记随 getRedirectUrl 转为 URL 凭证交给子站
		clearPendingCompliance();
		return { url: getComplianceUrl(tk), toCompliance: true, reviewAllowed };
	}
	return { url: getRedirectUrl(targetUrl, tk, cinfo), toCompliance: false, reviewAllowed };
}

// 预开工具标签：被浏览器拦截（手势已失效）时返回 null，由调用方降级
function openToolTab(win, tipText) {
	let tab = null;
	try {
		tab = win.open('about:blank', '_blank');
	} catch (e) {
		tab = null;
	}
	if (!tab) return null;
	// 判定要等一次接口往返，先铺一句等待提示，避免用户盯着空白页以为没反应
	try {
		tab.document.title = '特规通';
		const tip = tab.document.createElement('p');
		tip.textContent = tipText;
		tip.style.cssText = 'margin:0;padding:120px 0;text-align:center;font:14px/1.6 "Microsoft YaHei",sans-serif;color:#888';
		tab.document.body.appendChild(tip);
	} catch (e) {
		/* 空白页不可写时忽略，不影响后续跳转 */
	}
	return tab;
}

// 合规系统预开标签：等待文案固定为合规系统
function openComplianceTab(win) {
	return openToolTab(win, '正在打开文件合规审查系统，请稍候…');
}

// 鉴定评审占位标签：判定通过填正式地址，未通过关掉空白标签（预开被拦时 tab 为 null，走 pr 凭证子站兜底）
function fillOrCloseReviewTab(tab, allowed) {
	if (!tab) return;
	if (allowed) {
		tab.location.replace(REVIEW_URL);
	} else {
		try {
			tab.close();
		} catch (e) {
			/* ignore */
		}
	}
}

/**
 * 离站跳转统一入口：判定账户等级后，合规系统开新标签，个人版/企业版在当前标签整页替换
 * @param {string} targetUrl - 目标子站地址（SITE_URL.PERSON / SITE_URL.COMPANY）
 * @param {string} token - 登录 token
 * @param {object} [cinfo] - 企业信息（企业版跳转携带）
 * @param {object} [win] - 执行跳转的 window，默认当前窗口（微信回调在 iframe 内，需传 window.parent）
 * @param {boolean} [stayOnPlace] - 本次离站唯一目的就是合规（智能工具菜单点击）：合规开新标签后当前页原地不动；
 *                                 登录/注册/选企业等流程不传，合规另开标签的同时当前标签仍按原流程落到子站
 * @param {boolean} [wxLogin] - 微信扫码登录出口：几十秒扫码后回调已无手势，开新标签必被拦截——
 *                             不尝试 window.open，有权限时当前标签 location.replace 整页直达工具系统
 */
export async function goLoginRedirect(targetUrl, token, cinfo, win, stayOnPlace, wxLogin) {
	const opener = win || window;
	// 密码/验证码登录从点击到返回仅几百毫秒，window.open 仍在手势激活窗口内，标签可自开；
	// 微信扫码登录（wxLogin）不尝试开标签，直接走 replace 整页直达/子站兜底，三种登录方式各自行为确定
	const tab = !wxLogin && hasPendingCompliance() ? openComplianceTab(opener) : null;
	const reviewTab = !wxLogin && hasPendingReview() ? openToolTab(opener, '正在打开鉴定评审智能辅助系统，请稍候…') : null;
	const { url, toCompliance, reviewAllowed } = await resolveLoginRedirect(targetUrl, token, cinfo, !!reviewTab);

	if (toCompliance) {
		if (tab) {
			// replace 填地址：合规标签里只有这一条历史，后退按钮不可用
			tab.location.replace(url);
			// 鉴定评审：自开成功时填地址，失败/null 时标记已转 pr 交子站，此处无操作
			fillOrCloseReviewTab(reviewTab, reviewAllowed);
			// 智能工具菜单点击：当前页原地不动；登录/注册/选企业等流程：合规另开标签，当前标签仍按原流程落到子站
			// 标记已被判定消费，这里得到的是不带 pc 凭证的干净子站地址
			if (!stayOnPlace) opener.location.replace(getRedirectUrl(targetUrl, token || CUser.getToken(), cinfo));
			return;
		}
		// 标签自开被拦（手势已失效）/ 微信扫码登录：整页直达合规系统，不再兼顾子站；
		// 双标记时合规优先，评审标记保留在 sessionStorage，由日后回 tgt 的守卫消费兜底
		fillOrCloseReviewTab(reviewTab, reviewAllowed);
		opener.location.replace(url);
		return;
	}

	// 无权限/判定失败：合规没开成，关掉占位空白标签，当前标签按原流程落到子站
	if (tab) {
		try {
			tab.close();
		} catch (e) {
			/* ignore */
		}
	}
	fillOrCloseReviewTab(reviewTab, reviewAllowed);
	// 微信扫码登录 + 评审有权限：直接整页直达评审系统（不落子站，不再靠子站 confirm 供手势打开）
	if (wxLogin && reviewAllowed) {
		clearPendingReview();
		opener.location.replace(REVIEW_URL);
		return;
	}
	opener.location.replace(url);
}
